import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { SheetHeader, StatusDot } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  RefreshCw,
  History,
  Loader2,
  Search,
  Database,
} from 'lucide-react';
import { toast } from 'sonner';
// ponytail: date-fns fica só pelo texto aproximado ("há cerca de 2 horas"), que o Intl não tem.
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { API_URL } from '@/contexts/AuthContext';
import { formatBRL, formatDataHora } from '@/lib/format';

const fmtRelative = (iso) => {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return iso;
  }
};

const fmtAbsolute = (iso) => (iso ? formatDataHora(iso) || iso : '—');

const SyncDimensoesCard = () => {
  const [data, setData] = useState({ last: null, recent: [] });
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [hasNotified, setHasNotified] = useState(false);

  // Verificação dos dados gravados
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverage, setCoverage] = useState({ stats: null, items: [] });
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCoverage = useCallback(async (term = '') => {
    setCoverageLoading(true);
    try {
      const response = await axios.get(`${API_URL}/produtos/sync-dimensoes/coverage`, {
        params: { search: term || undefined, limit: 50 },
      });
      setCoverage(response.data || { stats: null, items: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar dados gravados');
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  const handleOpenCoverage = () => {
    setCoverageOpen(true);
    setSearchTerm('');
    fetchCoverage('');
  };

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/produtos/sync-dimensoes/status`);
      setData(response.data || { last: null, recent: [] });
    } catch (error) {
      // Não-master cai aqui — silenciar
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Notifica via toast quando o último sync falhou OU está muito antigo
  useEffect(() => {
    if (!data.last || hasNotified) return;

    const last = data.last;
    if (!last.success && last.errors > 0) {
      toast.error(
        `Sync de dimensões falhou: ${last.errors} erro(s). Os detalhes estão no cartão de sincronização de dimensões.`,
        { duration: 8000 },
      );
      setHasNotified(true);
    } else if (last.success) {
      // Sucesso silencioso — só mostra na UI, sem toast
      setHasNotified(true);
    }
  }, [data.last, hasNotified]);

  const handleRunNow = async () => {
    setTriggering(true);
    try {
      const response = await axios.post(`${API_URL}/produtos/sync-dimensoes/run-now`, {});
      toast.success(response.data?.message || 'Sync iniciado');
      // Re-fetcha depois de uns segundos pra mostrar resultado
      setTimeout(fetchStatus, 30 * 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar sync');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <SheetHeader title="Sincronização de dimensões, Filial 5" />
        <p className="px-5 py-4 text-sm text-gray-500">Carregando</p>
      </Card>
    );
  }

  const last = data.last;
  const ok = last?.success === true;
  const errored = last?.success === false;

  return (
    <>
      {/* No Dashboard a folha divide a faixa com o gráfico: estica até a altura dele e os botões descem para o pé. */}
      <Card className="flex flex-col">
        <SheetHeader title="Sincronização de dimensões, Filial 5">
          {ok && <StatusDot tone="ok">Em dia</StatusDot>}
          {errored && <StatusDot tone="bad">Com erros</StatusDot>}
          {!last && <StatusDot tone="off">Nunca executou</StatusDot>}
        </SheetHeader>
        <CardContent className="flex flex-1 flex-col gap-3 px-5 py-4">
          <p className="text-[13px] text-gray-500">Roda sozinha todo dia às 00:00, horário de Brasília.</p>
          {last ? (
            <>
              <p className="text-sm">
                {ok ? 'A última execução terminou sem erros.' : `A última execução falhou: ${last.errors} produto(s) com erro.`}
              </p>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-gray-500">Quando</p>
                  <p className="font-medium" title={fmtAbsolute(last.finished_at || last.started_at)}>
                    {fmtRelative(last.finished_at || last.started_at)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Atualizados</p>
                  <p className="font-medium text-green-700 dark:text-green-300">
                    {last.updated} / {last.total}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Sem dados no Tiny</p>
                  <p className="font-medium text-yellow-700 dark:text-yellow-300">
                    {last.skipped_no_data || 0}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Duração</p>
                  <p className="font-medium tabular-nums">
                    {Math.round((last.duration_seconds || 0))}s
                  </p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Nenhuma sincronização ainda. Clique em "Sincronizar agora" para a primeira execução.
            </p>
          )}

          <div className="mt-auto flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              onClick={handleRunNow}
              disabled={triggering}
              className="gap-1"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${triggering ? 'animate-spin' : ''}`} />
              {triggering ? 'Iniciando...' : 'Sincronizar agora'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenCoverage}
              className="gap-1"
            >
              <Database className="h-3.5 w-3.5" />
              Ver dados gravados
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setHistoryOpen(true)}
              disabled={!data.recent.length}
              className="gap-1"
            >
              <History className="h-3.5 w-3.5" />
              Ver histórico
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={coverageOpen} onOpenChange={setCoverageOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dados gravados, Filial 5</DialogTitle>
            <DialogDescription>
              Estatísticas de cobertura e amostra dos produtos com dimensões registradas no Postgres.
            </DialogDescription>
          </DialogHeader>

          {/* Stats */}
          {coverage.stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              <div className="rounded-lg bg-sheet2 px-3 py-3">
                  <p className="text-xs text-gray-500">Total na Filial 5</p>
                  <p className="text-2xl font-medium tabular-nums">{coverage.stats.total}</p>
              </div>
              <div className="rounded-lg bg-sheet2 px-3 py-3">
                  <p className="text-xs text-gray-500">Com peso bruto</p>
                  <p className="text-2xl font-medium tabular-nums text-green-600 dark:text-green-400">
                    {coverage.stats.com_peso}
                  </p>
                  {coverage.stats.total > 0 && (
                    <p className="text-[10px] text-gray-500">
                      {Math.round((coverage.stats.com_peso / coverage.stats.total) * 100)}% da Filial 5
                    </p>
                  )}
              </div>
              <div className="rounded-lg bg-sheet2 px-3 py-3">
                  <p className="text-xs text-gray-500">Com TODAS dimensões</p>
                  <p className="text-2xl font-medium tabular-nums text-blue-600 dark:text-blue-400">
                    {coverage.stats.com_todas_dimensoes}
                  </p>
              </div>
              <div className="rounded-lg bg-sheet2 px-3 py-3">
                  <p className="text-xs text-gray-500">Sem nenhuma dimensão</p>
                  <p className="text-2xl font-medium tabular-nums text-yellow-600 dark:text-yellow-400">
                    {coverage.stats.sem_nada}
                  </p>
              </div>
            </div>
          )}

          {/* Busca */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por SKU ou nome do produto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchCoverage(searchTerm)}
              className="pl-9"
            />
          </div>

          {/* Tabela */}
          <div className="mt-2 overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader className="bg-sheet2">
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-right">Peso (kg)</TableHead>
                  <TableHead className="text-right">L × A × C (cm)</TableHead>
                  <TableHead>Sync</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coverageLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                    </TableCell>
                  </TableRow>
                ) : coverage.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-gray-500">
                      Nenhum produto encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  coverage.items.map((it) => (
                    <TableRow key={it.sku}>
                      <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate" title={it.nome}>
                        {it.nome || '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {it.preco != null && it.preco > 0 ? (
                          <span className="text-foreground">
                            {formatBRL(it.preco)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {it.peso_bruto != null ? (
                          <span className="text-green-700 dark:text-green-300">
                            {it.peso_bruto.toFixed(3)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {it.largura != null && it.altura != null && it.comprimento != null ? (
                          <span className="text-blue-700 dark:text-blue-300">
                            {it.largura.toFixed(1)} × {it.altura.toFixed(1)} × {it.comprimento.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500" title={fmtAbsolute(it.dimensoes_sync_at)}>
                        {it.dimensoes_sync_at ? fmtRelative(it.dimensoes_sync_at) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-gray-500">
            Mostrando até {coverage.limit ?? 50} resultados. Aperte Enter no campo de busca para filtrar.
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico de sincronizações</DialogTitle>
            <DialogDescription>Últimas {data.recent.length} execuções (mais recente primeiro)</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            {data.recent.map((s) => (
              <div
                key={s.id}
                className="rounded-lg border border-border p-3 text-sm"
              >
                <div className="flex items-center justify-between mb-2">
                  <StatusDot tone={s.success ? 'ok' : 'bad'}>{fmtAbsolute(s.started_at)}</StatusDot>
                  <Badge variant="outline" className="text-[10px]">
                    {s.triggered_by}
                  </Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Total:</span> <strong>{s.total}</strong>
                  </div>
                  <div>
                    <span className="text-gray-500">OK:</span>{' '}
                    <strong className="text-green-700 dark:text-green-300">{s.updated}</strong>
                  </div>
                  <div>
                    <span className="text-gray-500">Sem dados:</span>{' '}
                    <strong className="text-yellow-700 dark:text-yellow-300">
                      {s.skipped_no_data || 0}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-500">Erros:</span>{' '}
                    <strong className="text-red-700 dark:text-red-300">{s.errors}</strong>
                  </div>
                </div>
              </div>
            ))}
            {!data.recent.length && (
              <p className="text-sm text-gray-500 text-center py-4">Nenhuma execução registrada.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SyncDimensoesCard;
